import React, { useEffect, useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import { TimberProject, User } from '../types';
import { db } from '../utils/firebase';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove, onSnapshot, query, orderBy } from 'firebase/firestore';
import AppHeader from '../components/ui/AppHeader';
import '../styles/ClientManagement.css';

type UserRole = 'employee' | 'client' | 'viewer' | 'user';

interface AuthenticatedUser {
  uid: string;
  email: string;
  displayName: string;
  authProvider: string;
  role: UserRole;
  createdAt: Date;
  lastLogin: Date;
}

interface EditingClient {
  id: string;
  name: string;
  email: string;
}

const ROLE_OPTIONS: { value: UserRole; label: string; description: string }[] = [
  { value: 'employee', label: 'Employee', description: 'Full access to all CRM features' },
  { value: 'client', label: 'Client', description: 'Can view their own projects' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to assigned data' },
  { value: 'user', label: 'User', description: 'Basic authenticated user' },
];

interface ProjectWithId extends TimberProject {
  id: string;
}

const ClientManagement: React.FC = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState<User[]>([]);
  const [authenticatedUsers, setAuthenticatedUsers] = useState<AuthenticatedUser[]>([]);
  const [projects, setProjects] = useState<ProjectWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'clients' | 'users' | 'assignments' | 'permissions'>('clients');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [editingClient, setEditingClient] = useState<EditingClient | null>(null);
  const [editingUser, setEditingUser] = useState<AuthenticatedUser | null>(null);
  const [changingRole, setChangingRole] = useState<string | null>(null);

  // Set up real-time listeners
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Clients listener
    const clientsUnsubscribe = onSnapshot(
      collection(db, 'clients'),
      (snapshot) => {
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as User));
        setClients(clientsData);
      }
    );
    unsubscribers.push(clientsUnsubscribe);

    // Authenticated users listener
    const authUsersUnsubscribe = onSnapshot(
      collection(db, 'authenticatedUsers'),
      (snapshot) => {
        const authUsersData = snapshot.docs.map(doc => ({
          uid: doc.id,
          role: 'user' as UserRole,
          ...doc.data()
        } as AuthenticatedUser));
        setAuthenticatedUsers(authUsersData);
      }
    );
    unsubscribers.push(authUsersUnsubscribe);

    // Timber Projects listener (real-time, same as CRMContext)
    const projectsQuery = query(collection(db, 'timberProjects'), orderBy('createdAt', 'desc'));
    const projectsUnsubscribe = onSnapshot(
      projectsQuery,
      (snapshot) => {
        const projectsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            startDate: data.startDate?.toDate?.() || new Date(),
            targetEndDate: data.targetEndDate?.toDate?.() || null,
            actualEndDate: data.actualEndDate?.toDate?.() || null,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
          } as ProjectWithId;
        });
        setProjects(projectsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching projects:', error);
        setLoading(false);
      }
    );
    unsubscribers.push(projectsUnsubscribe);

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, []);

  const fetchData = async () => {
    // This is now handled by real-time listeners, but kept for manual refresh if needed
  };

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    setChangingRole(userId);
    try {
      const userRef = doc(db, 'authenticatedUsers', userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: user?.email || 'system',
      });
      
      // If promoting to client, also create client record
      if (newRole === 'client') {
        const authUser = authenticatedUsers.find(u => u.uid === userId);
        if (authUser) {
          await setDoc(doc(db, 'clients', userId), {
            email: authUser.email,
            name: authUser.displayName || authUser.email.split('@')[0],
            createdAt: new Date(),
            promotedAt: new Date(),
            promotedBy: user?.email || 'system',
            authProvider: authUser.authProvider,
          });
        }
      }
      // Real-time listener will update the state automatically
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update role. Please try again.');
    } finally {
      setChangingRole(null);
    }
  };

  const promoteToClient = async (authUser: AuthenticatedUser) => {
    try {
      const clientData = {
        email: authUser.email,
        name: authUser.displayName || authUser.email.split('@')[0],
        createdAt: new Date(),
        promotedAt: new Date(),
        promotedBy: user?.email || 'system',
        authProvider: authUser.authProvider,
      };

      await setDoc(doc(db, 'clients', authUser.uid), clientData);
      
      // Also update their role in authenticatedUsers
      await updateDoc(doc(db, 'authenticatedUsers', authUser.uid), {
        role: 'client',
        updatedAt: new Date(),
      });
      // Real-time listener will update the state automatically
    } catch (error) {
      console.error('Error promoting user to client:', error);
    }
  };

  const updateClient = async () => {
    if (!editingClient) return;
    
    try {
      const clientRef = doc(db, 'clients', editingClient.id);
      await updateDoc(clientRef, {
        name: editingClient.name,
        email: editingClient.email,
        updatedAt: new Date(),
        updatedBy: user?.email || 'system',
      });
      setEditingClient(null);
      // Real-time listener will update the state automatically
    } catch (error) {
      console.error('Error updating client:', error);
    }
  };

  const deleteClient = async (clientId: string) => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'clients', clientId));
      // Real-time listener will update the state automatically
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const updateAuthUser = async () => {
    if (!editingUser) return;
    
    try {
      const userRef = doc(db, 'authenticatedUsers', editingUser.uid);
      await updateDoc(userRef, {
        displayName: editingUser.displayName,
        email: editingUser.email,
        updatedAt: new Date(),
      });
      setEditingUser(null);
      // Real-time listener will update the state automatically
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const deleteAuthUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'authenticatedUsers', userId));
      // Also delete from clients if exists
      try {
        await deleteDoc(doc(db, 'clients', userId));
      } catch {
        // Client may not exist, ignore
      }
      // Real-time listener will update the state automatically
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const assignClientToProject = async () => {
    if (!selectedClientId || !selectedProjectId) return;
    
    const client = clients.find(c => c.id === selectedClientId);
    if (!client) return;

    try {
      const projectRef = doc(db, 'timberProjects', selectedProjectId);
      await updateDoc(projectRef, {
        clientIds: arrayUnion(client.email)
      });
      // Real-time listener will update the state automatically
      setSelectedClientId(null);
      setSelectedProjectId(null);
    } catch (error) {
      console.error('Error assigning client to project:', error);
    }
  };

  const removeClientFromProject = async (clientEmail: string, projectId: string) => {
    try {
      const projectRef = doc(db, 'timberProjects', projectId);
      await updateDoc(projectRef, {
        clientIds: arrayRemove(clientEmail)
      });
      // Real-time listener will update the state automatically
    } catch (error) {
      console.error('Error removing client from project:', error);
    }
  };

  const getClientProjectCount = (clientId: string): number => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return 0;
    return projects.filter(p => p.clientIds?.includes(client.email)).length;
  };

  const getRoleBadgeClass = (role: UserRole): string => {
    return `badge ${role}`;
  };

  if (loading) {
    return (
      <div className="client-management-page">
        <AppHeader />
        <div className="loading-state">Loading client data...</div>
      </div>
    );
  }

  return (
    <div className="client-management-page">
      <AppHeader />

      <div className="page-content">
        <div className="page-header">
          <h1>Client Management</h1>
          <p>Manage clients, users, permissions, and project assignments</p>
        </div>

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'clients' ? 'active' : ''}`}
            onClick={() => setActiveTab('clients')}
          >
            Clients ({clients.length})
          </button>
          <button 
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            All Users ({authenticatedUsers.length})
          </button>
          <button 
            className={`tab ${activeTab === 'assignments' ? 'active' : ''}`}
            onClick={() => setActiveTab('assignments')}
          >
            Project Assignments
          </button>
          <button 
            className={`tab ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            Permissions
          </button>
        </div>

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="table-container">
            {clients.length === 0 ? (
              <div className="empty-state">
                <p>No clients yet. Change a user's role to "Client" from the "All Users" tab.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Projects</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(client => (
                    <tr key={client.id}>
                      <td className="name-cell">
                        {editingClient?.id === client.id ? (
                          <input
                            type="text"
                            value={editingClient.name}
                            onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
                            className="edit-input"
                          />
                        ) : (
                          client.name
                        )}
                      </td>
                      <td>
                        {editingClient?.id === client.id ? (
                          <input
                            type="email"
                            value={editingClient.email}
                            onChange={(e) => setEditingClient({ ...editingClient, email: e.target.value })}
                            className="edit-input"
                          />
                        ) : (
                          client.email
                        )}
                      </td>
                      <td>{getClientProjectCount(client.id)}</td>
                      <td>
                        <span className="badge active">Active</span>
                      </td>
                      <td className="actions-cell">
                        {editingClient?.id === client.id ? (
                          <>
                            <button className="action-btn save" onClick={updateClient}>Save</button>
                            <button className="action-btn cancel" onClick={() => setEditingClient(null)}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="action-btn edit"
                              onClick={() => setEditingClient({ id: client.id, name: client.name, email: client.email })}
                            >
                              Edit
                            </button>
                            <button 
                              className="action-btn delete"
                              onClick={() => deleteClient(client.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* All Users Tab */}
        {activeTab === 'users' && (
          <div className="table-container">
            {authenticatedUsers.length === 0 ? (
              <div className="empty-state">
                <p>No authenticated users yet. Users appear here after signing in.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Provider</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {authenticatedUsers.map(authUser => {
                    return (
                      <tr key={authUser.uid}>
                        <td className="name-cell">
                          {editingUser?.uid === authUser.uid ? (
                            <input
                              type="text"
                              value={editingUser.displayName}
                              onChange={(e) => setEditingUser({ ...editingUser, displayName: e.target.value })}
                              className="edit-input"
                            />
                          ) : (
                            authUser.displayName || '—'
                          )}
                        </td>
                        <td>
                          {editingUser?.uid === authUser.uid ? (
                            <input
                              type="email"
                              value={editingUser.email}
                              onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                              className="edit-input"
                            />
                          ) : (
                            authUser.email
                          )}
                        </td>
                        <td>{authUser.authProvider}</td>
                        <td>
                          <select
                            className="role-selector"
                            value={authUser.role || 'user'}
                            onChange={(e) => updateUserRole(authUser.uid, e.target.value as UserRole)}
                            disabled={changingRole === authUser.uid}
                          >
                            {ROLE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="actions-cell">
                          {editingUser?.uid === authUser.uid ? (
                            <>
                              <button className="action-btn save" onClick={updateAuthUser}>Save</button>
                              <button className="action-btn cancel" onClick={() => setEditingUser(null)}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="action-btn edit"
                                onClick={() => setEditingUser({ ...authUser })}
                              >
                                Edit
                              </button>
                              <button 
                                className="action-btn delete"
                                onClick={() => deleteAuthUser(authUser.uid)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="assignments-section">
            <div className="assignment-form">
              <h3>Assign Client to Project</h3>
              <div className="form-row">
                <select 
                  value={selectedClientId || ''}
                  onChange={(e) => setSelectedClientId(e.target.value || null)}
                >
                  <option value="">Select Client</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                  ))}
                </select>
                <select 
                  value={selectedProjectId || ''}
                  onChange={(e) => setSelectedProjectId(e.target.value || null)}
                >
                  <option value="">Select Project</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button 
                  className="action-btn primary"
                  disabled={!selectedClientId || !selectedProjectId}
                  onClick={assignClientToProject}
                >
                  Assign
                </button>
              </div>
            </div>

            <h3>Current Assignments</h3>
            <div className="table-container">
              {projects.length === 0 ? (
                <div className="empty-state">
                  <p>No projects yet. Create projects from the Projects page.</p>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Status</th>
                      <th>Assigned Clients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map(project => (
                      <tr key={project.id}>
                        <td className="name-cell">{project.name}</td>
                        <td>
                          <span className={`badge ${project.status}`}>
                            {project.status.replace('-', ' ')}
                          </span>
                        </td>
                        <td>
                          <div className="assigned-clients">
                            {project.clientIds?.length ? (
                              project.clientIds.map(email => {
                                const client = clients.find(c => c.email === email);
                                return (
                                  <span key={email} className="client-tag">
                                    {client?.name || email}
                                    <button 
                                      className="remove-btn"
                                      onClick={() => removeClientFromProject(email, project.id)}
                                      title="Remove client"
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })
                            ) : (
                              <span className="no-clients">No clients assigned</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Permissions Tab */}
        {activeTab === 'permissions' && (
          <div className="permissions-section">
            <div className="table-container">
              <h3 style={{ padding: '16px', margin: 0, borderBottom: '1px solid #f0f0f0' }}>Role Permissions</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Employee</th>
                    <th>Client</th>
                    <th>Viewer</th>
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="name-cell">View Map & Leads</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">Create/Edit Leads</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">View Deal Pipeline</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">Create/Edit Deals</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">View Projects</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">Edit Projects</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">View Reports</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">Export Reports</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">View Analytics</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">Manage Users</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">API Access</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                  <tr>
                    <td className="name-cell">Settings</td>
                    <td><span className="badge active">✓</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                    <td><span className="badge pending">—</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="table-container" style={{ marginTop: '24px' }}>
              <h3 style={{ padding: '16px', margin: 0, borderBottom: '1px solid #f0f0f0' }}>Role Descriptions</h3>
              <div style={{ padding: '16px' }}>
                {ROLE_OPTIONS.map(role => (
                  <div key={role.value} style={{ marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <span className={getRoleBadgeClass(role.value)} style={{ minWidth: '80px', textAlign: 'center' }}>
                      {role.label}
                    </span>
                    <span style={{ color: '#666', fontSize: '14px' }}>{role.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManagement;
